/**
 * Preload Service
 *
 * Handles preloading of critical data on app initialization.
 * Uses parallel fetching for optimal performance and graceful error handling
 * to prevent blocking if individual endpoints fail.
 */

import { referenceDataService } from './referenceDataService';
import { useCargoCountStore } from '@/store/cargoCountStore';
import { useCargoListStore } from '@/store/cargoListStore';
import { useBalticStore } from '@/store/balticStore';
import { useBunkerPricesStore } from '@/store/bunkerPricesStore';
import { useFixedDaysStore } from '@/store/fixedDaysStore';
import { usePositionListStore } from '@/store/positionListStore';
import { useFixturesStore } from '@/store/fixturesStore';
import { useRecentFixturesStore } from '@/store/recentFixturesStore';
import { useUserGroupsStore } from '@/store/userGroupsStore';
import { UserRole } from '@/permissions/types';

export interface PreloadResult {
  success: boolean;
  loaded: string[];
  failed: string[];
  errors: Record<string, string>;
}

/**
 * Preload critical data that should be available immediately after login
 *
 * This function:
 * - Fetches data in parallel for optimal performance
 * - Gracefully handles errors without blocking
 * - Updates relevant Zustand stores
 * - Returns detailed status of each operation
 */
export async function preloadCriticalData(userRoles: UserRole[] = []): Promise<PreloadResult> {
  const loaded: string[] = [];
  const failed: string[] = [];
  const errors: Record<string, string> = {};

  const hasRole = (role: UserRole) => userRoles.includes(role);
  const hasAnyRole = (...roles: UserRole[]) => roles.some((r) => userRoles.includes(r));

  // Build preload operations based on user's roles
  const preloadOperations: { name: string; operation: () => Promise<void> }[] = [];

  // Reference Data — only needed for Cargo List itinerary forms
  if (hasRole(UserRole.CARGO_LIST)) {
    preloadOperations.push(
      {
        name: 'ports',
        operation: async () => {
          await referenceDataService.getPorts();
          loaded.push('ports');
        },
      },
      {
        name: 'purposes',
        operation: async () => {
          await referenceDataService.getPurposes();
          loaded.push('purposes');
        },
      },
      {
        name: 'viapoints',
        operation: async () => {
          await referenceDataService.getViaPoints();
          loaded.push('viapoints');
        },
      },
      {
        name: 'cargoList',
        operation: async () => {
          const { fetchCargoList } = useCargoListStore.getState();
          await fetchCargoList();
          loaded.push('cargoList');
        },
      }
    );
  }

  if (hasRole(UserRole.CARGO_COUNT)) {
    preloadOperations.push({
      name: 'cargoCount',
      operation: async () => {
        const { fetchCargoCount } = useCargoCountStore.getState();
        await fetchCargoCount();
        loaded.push('cargoCount');
      },
    });
  }

  if (hasRole(UserRole.BALTIC)) {
    preloadOperations.push({
      name: 'balticRoutes',
      operation: async () => {
        const { fetchRoutes } = useBalticStore.getState();
        await fetchRoutes();
        loaded.push('balticRoutes');
      },
    });
  }

  if (hasRole(UserRole.BUNKER_PRICES)) {
    preloadOperations.push({
      name: 'bunkerPrices',
      operation: async () => {
        const { fetchBunkerPrices } = useBunkerPricesStore.getState();
        await fetchBunkerPrices();
        loaded.push('bunkerPrices');
      },
    });
  }

  if (hasRole(UserRole.FIXED_DAYS)) {
    preloadOperations.push({
      name: 'fixedDaysPools',
      operation: async () => {
        const { fetchPools } = useFixedDaysStore.getState();
        await fetchPools();
        loaded.push('fixedDaysPools');
      },
    });
  }

  if (hasRole(UserRole.POSITION_LIST)) {
    preloadOperations.push({
      name: 'positionListAreas',
      operation: async () => {
        const { fetchAreas } = usePositionListStore.getState();
        await fetchAreas();
        loaded.push('positionListAreas');
      },
    });
  }

  if (hasAnyRole(UserRole.FIXTURES, UserRole.RECENT_FIXTURES)) {
    preloadOperations.push(
      {
        name: 'fixtures',
        operation: async () => {
          const { fetchFixtures } = useFixturesStore.getState();
          await fetchFixtures();
          loaded.push('fixtures');
        },
      },
      {
        name: 'recentFixtures',
        operation: async () => {
          const { fetchFixtures } = useRecentFixturesStore.getState();
          await fetchFixtures();
          loaded.push('recentFixtures');
        },
      }
    );
  }

  if (hasAnyRole(UserRole.USERS, UserRole.USER_GROUPS)) {
    preloadOperations.push({
      name: 'userGroups',
      operation: async () => {
        const { fetchUserGroupsAndRoles } = useUserGroupsStore.getState();
        await fetchUserGroupsAndRoles();
        loaded.push('userGroups');
      },
    });
  }

  // Execute all operations in parallel with individual error handling
  await Promise.allSettled(
    preloadOperations.map(async ({ name, operation }) => {
      try {
        await operation();
      } catch (error) {
        failed.push(name);
        errors[name] = error instanceof Error ? error.message : `Failed to load ${name}`;
      }
    })
  );

  const success = failed.length === 0;

  return {
    success,
    loaded,
    failed,
    errors,
  };
}

/**
 * Preload optional data (non-blocking, can be done in background)
 *
 * This includes data that's nice to have cached but not critical
 * for initial app render.
 */
export async function preloadOptionalData(): Promise<void> {
  // These can fail silently as they're not critical
  const optionalOperations: Promise<unknown>[] = [
    // Add optional data fetches here if needed
    // For now, we'll let individual pages handle their own data loading
  ];

  await Promise.allSettled(optionalOperations);
}

/**
 * Critical data is never cached — always returns false to trigger preloading.
 */
export function isCriticalDataCached(): boolean {
  return false;
}
